<template>
  <div class="survey-entry-page">
    <div class="survey-entry-card">
      <div class="header-section">
        <div class="emblem-container">
          <i class="el-icon-document-checked emblem-icon"></i>
        </div>
        <h2 class="title">Опитування споживачів природного газу</h2>
        <p class="subtitle">
          Оцінка рівня задоволеності якістю послуг Оператора ГРМ відповідно до вимог НКРЕКП
        </p>
      </div>

      <el-alert
        title="Офіційне опитування НКРЕКП"
        type="info"
        description="Опитування проводиться у зв'язку із запитом Національної комісії, що здійснює державне регулювання у сферах енергетики та комунальних послуг (НКРЕКП). Усі надані відповіді є конфіденційними."
        show-icon
        :closable="false"
        class="info-alert"
      />

      <!-- Auto-identifying Banner for URL transitions -->
      <div v-if="isAutoVerifying" class="auto-verify-card">
        <div class="loading-spinner-wrap">
          <i class="el-icon-loading auto-spin-icon"></i>
        </div>
        <div class="auto-verify-text">
          <div class="auto-verify-title">Ідентифікація побутового споживача...</div>
          <div class="auto-verify-desc">
            Особовий рахунок: <strong class="account-badge">{{ form.accountNumber }}</strong>. Підтягуємо персональний EIC-код та відкриваємо опитування...
          </div>
        </div>
      </div>

      <el-form
        v-show="!isAutoVerifying"
        :model="form"
        :rules="rules"
        ref="entryForm"
        label-position="top"
        class="entry-form"
        @submit.native.prevent="handleVerify"
      >
        <!-- Locked consumer type indicator for direct transitions (no radio buttons) -->
        <div v-if="isTypeLocked" class="locked-type-banner">
          <div class="locked-type-tag" :class="form.surveyType">
            <i :class="form.surveyType === 'household' ? 'el-icon-user' : 'el-icon-office-building'"></i>
            <span>{{ form.surveyType === 'household' ? 'Опитування для побутових споживачів (населення)' : 'Опитування для непобутових споживачів (юридичні особи / ФОП)' }}</span>
          </div>
        </div>

        <!-- Generic consumer type selector if not locked -->
        <el-form-item v-else label="Оберіть тип споживача" prop="surveyType">
          <el-radio-group v-model="form.surveyType" class="type-selector" size="medium" @change="handleTypeChange">
            <el-radio-button label="household">
              <i class="el-icon-user"></i> Побутовий споживач (населення)
            </el-radio-button>
            <el-radio-button label="non_household">
              <i class="el-icon-office-building"></i> Непобутовий споживач (юридичні особи / ФОП)
            </el-radio-button>
          </el-radio-group>
        </el-form-item>

        <!-- Household Identification Mode Selector -->
        <div v-if="form.surveyType === 'household'" class="method-selector-wrap">
          <div class="method-label">Спосіб ідентифікації:</div>
          <el-radio-group v-model="identificationMethod" size="small" class="method-group">
            <el-radio-button label="account">
              <i class="el-icon-document"></i> За особовим рахунком
            </el-radio-button>
            <el-radio-button label="eic">
              <i class="el-icon-key"></i> За EIC-кодом
            </el-radio-button>
          </el-radio-group>
        </div>

        <!-- Mode A: By Account Number (Recommended for household) -->
        <el-form-item
          v-if="form.surveyType === 'household' && identificationMethod === 'account'"
          label="Введіть номер особового рахунку"
          prop="accountNumber"
        >
          <el-input
            v-model="form.accountNumber"
            placeholder="Наприклад: 12004567"
            maxlength="25"
            prefix-icon="el-icon-document"
            clearable
            @input="handleAccountInput"
          >
          </el-input>
          <div class="field-hint">
            <i class="el-icon-info"></i> Номер особового рахунку вказано у рахунку (квитанції) на оплату за розподіл газу або в особистому кабінеті. Персональний EIC-код підтягнеться автоматично.
          </div>
        </el-form-item>

        <!-- Mode B: By 16-character EIC Code -->
        <el-form-item
          v-else
          :label="form.surveyType === 'household' ? 'Введіть Ваш EIC-код (персональний код точки обліку газу)' : 'Введіть EIC-код підприємства / точки обліку газу'"
          prop="eic"
        >
          <el-input
            v-model="form.eic"
            placeholder="Наприклад: 56XM01001234567A"
            maxlength="20"
            prefix-icon="el-icon-key"
            clearable
            @input="handleEicInput"
          >
          </el-input>
          <div class="field-hint">
            <i class="el-icon-info"></i> 16-значний код точки обліку, вказаний у договорі або рахунку за розподіл газу.
          </div>
        </el-form-item>

        <div v-if="errorMessage" class="error-box">
          <el-alert
            :title="errorMessage"
            :type="alreadyVoted ? 'warning' : 'error'"
            show-icon
            :closable="false"
          >
            <div v-if="mismatchType" style="margin-top: 10px;">
              <el-button
                size="mini"
                type="primary"
                plain
                icon="el-icon-refresh"
                @click="switchTypeAndRetry"
              >
                Перейти до опитування для {{ mismatchType === 'household' ? 'побутових' : 'непобутових' }} споживачів
              </el-button>
            </div>
            <div v-if="notFoundWithAccount && identificationMethod === 'account'" style="margin-top: 10px;">
              <el-button
                size="mini"
                type="info"
                plain
                icon="el-icon-key"
                @click="switchToEicMethod"
              >
                Ввести 16-значний EIC-код вручну
              </el-button>
            </div>
          </el-alert>
        </div>

        <el-form-item class="action-item">
          <el-button
            type="primary"
            class="submit-btn"
            size="large"
            :loading="isLoading || isAutoVerifying"
            @click="handleVerify"
          >
            Перейти до опитування <i class="el-icon-right"></i>
          </el-button>
        </el-form-item>
      </el-form>

      <div class="footer-note">
        <span>Вже зареєстровані в системі? </span>
        <router-link to="/login" class="login-link">Увійти до особистого кабінету</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'SurveyEntry',
  data() {
    return {
      identificationMethod: 'account', // 'account' | 'eic'
      isAutoVerifying: false,
      isTypeLocked: false,
      notFoundWithAccount: false,
      form: {
        accountNumber: '',
        eic: '',
        surveyType: 'household',
      },
      errorMessage: '',
      alreadyVoted: false,
      mismatchType: null,
      rules: {
        surveyType: [
          { required: true, message: 'Будь ласка, оберіть тип споживача', trigger: 'change' },
        ],
        accountNumber: [
          { required: true, message: 'Будь ласка, введіть номер особового рахунку', trigger: 'blur' },
          { min: 3, message: 'Номер особового рахунку повинен містити щонайменше 3 символи', trigger: 'blur' },
        ],
        eic: [
          { required: true, message: 'Будь ласка, введіть EIC-код', trigger: 'blur' },
          { min: 10, message: 'EIC-код повинен містити щонайменше 10 символів', trigger: 'blur' },
        ],
      },
    };
  },
  computed: {
    ...mapGetters('Survey', ['isLoading']),
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        this.initFromRoute(to);
      },
    },
  },
  methods: {
    ...mapActions('Survey', ['verifyEic']),

    initFromRoute(route) {
      if (!route) route = this.$route;
      const path = route.path || '';
      const query = route.query || {};

      this.errorMessage = '';
      this.alreadyVoted = false;
      this.mismatchType = null;
      this.notFoundWithAccount = false;

      // 1. Detect route path specifics (locks type)
      if (path.includes('non-household')) {
        this.form.surveyType = 'non_household';
        this.identificationMethod = 'eic';
        this.isTypeLocked = true;
      } else if (path.includes('household')) {
        this.form.surveyType = 'household';
        this.isTypeLocked = true;
      } else {
        this.isTypeLocked = false;
      }

      // 2. Query param overrides for consumer type
      if (query.type) {
        const t = String(query.type).toLowerCase();
        if (t === 'non_household' || t === 'commercial' || t === 'non-household') {
          this.form.surveyType = 'non_household';
          this.identificationMethod = 'eic';
        } else {
          this.form.surveyType = 'household';
        }
        this.isTypeLocked = true;
      }

      // 3. Detect account number passed from external site
      const accountParam = query.accountNumber ||
                           query.account ||
                           query.contractNumber ||
                           query.acc ||
                           query.rahunok ||
                           query.ls;

      if (accountParam) {
        this.form.accountNumber = String(accountParam).trim();
        this.form.surveyType = 'household';
        this.identificationMethod = 'account';
        this.isTypeLocked = true;
        // Automatically pull EIC and identify the consumer!
        this.autoIdentifyConsumer({ accountNumber: this.form.accountNumber });
        return;
      }

      // 4. Detect EIC passed in URL
      if (query.eic) {
        this.form.eic = String(query.eic).toUpperCase().replace(/\s/g, '');
        this.identificationMethod = 'eic';
        if (this.form.eic.length >= 10) {
          this.autoIdentifyConsumer({ eic: this.form.eic });
        }
      }
    },

    handleAccountInput(val) {
      this.form.accountNumber = String(val).trim();
      this.errorMessage = '';
      this.alreadyVoted = false;
      this.notFoundWithAccount = false;
    },

    handleEicInput(val) {
      this.form.eic = val.toUpperCase().replace(/\s/g, '');
      this.errorMessage = '';
      this.alreadyVoted = false;
      this.mismatchType = null;
    },

    handleTypeChange(val) {
      this.errorMessage = '';
      this.alreadyVoted = false;
      this.mismatchType = null;
      if (val === 'non_household') {
        this.identificationMethod = 'eic';
      }
    },

    switchToEicMethod() {
      this.identificationMethod = 'eic';
      this.errorMessage = '';
      this.notFoundWithAccount = false;
    },

    switchTypeAndRetry() {
      if (this.mismatchType) {
        this.form.surveyType = this.mismatchType;
        if (this.mismatchType === 'non_household') {
          this.identificationMethod = 'eic';
        }
        this.errorMessage = '';
        this.mismatchType = null;
        this.alreadyVoted = false;
        this.handleVerify();
      }
    },

    async autoIdentifyConsumer({ accountNumber, eic }) {
      this.isAutoVerifying = true;
      this.errorMessage = '';
      this.alreadyVoted = false;
      this.mismatchType = null;
      this.notFoundWithAccount = false;

      try {
        const payload = {
          surveyType: this.form.surveyType,
        };
        if (accountNumber) payload.accountNumber = accountNumber;
        if (eic) payload.eic = eic;

        const result = await this.verifyEic(payload);

        if (result && result.surveyId) {
          const resolvedEic = result.eic || (result.consumer && result.consumer.eic);
          const resolvedAccount = result.accountNumber || (result.consumer && result.consumer.contractNumber) || accountNumber || '';

          this.$router.push({
            name: 'survey-form',
            params: { id: result.surveyId },
            query: {
              eic: resolvedEic,
              accountNumber: resolvedAccount,
              entry: 'web_link',
            },
          });
        }
      } catch (err) {
        this.isAutoVerifying = false;
        if (err.response && err.response.data) {
          const data = err.response.data;
          this.errorMessage = data.error || 'Помилка ідентифікації споживача';
          if (data.alreadyVoted) {
            this.alreadyVoted = true;
          }
          if (data.notFound) {
            this.notFoundWithAccount = !!accountNumber;
          }
          if (data.categoryMismatch && data.actualType) {
            this.mismatchType = data.actualType;
          }
        } else {
          this.errorMessage = 'Не вдалося перевірити дані споживача. Перевірте підключення або введіть дані вручну.';
        }
      }
    },

    async handleVerify() {
      this.errorMessage = '';
      this.alreadyVoted = false;
      this.mismatchType = null;
      this.notFoundWithAccount = false;

      const isAccountMode = (this.form.surveyType === 'household' && this.identificationMethod === 'account');
      const fieldToValidate = isAccountMode ? 'accountNumber' : 'eic';

      this.$refs.entryForm.validateField(fieldToValidate, async (error) => {
        if (error) return;

        try {
          const payload = {
            surveyType: this.form.surveyType,
          };
          if (isAccountMode) {
            payload.accountNumber = this.form.accountNumber;
          } else {
            payload.eic = this.form.eic;
          }

          const result = await this.verifyEic(payload);

          if (result && result.surveyId) {
            const resolvedEic = result.eic || (result.consumer && result.consumer.eic) || this.form.eic;
            const resolvedAccount = result.accountNumber || (result.consumer && result.consumer.contractNumber) || this.form.accountNumber || '';

            this.$router.push({
              name: 'survey-form',
              params: { id: result.surveyId },
              query: {
                eic: resolvedEic,
                accountNumber: resolvedAccount,
                entry: 'web_link',
              },
            });
          }
        } catch (err) {
          if (err.response && err.response.data) {
            const data = err.response.data;
            this.errorMessage = data.error || 'Помилка перевірки даних споживача';
            if (data.alreadyVoted) {
              this.alreadyVoted = true;
            }
            if (data.notFound) {
              this.notFoundWithAccount = isAccountMode;
            }
            if (data.categoryMismatch && data.actualType) {
              this.mismatchType = data.actualType;
            }
          } else {
            this.errorMessage = 'Не вдалося перевірити дані споживача. Перевірте з\'єднання з сервером.';
          }
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.survey-entry-page {
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 15px;
  background: #ffffff;
}

.survey-entry-card {
  max-width: 580px;
  width: 100%;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  padding: 24px 28px;
  background: #ffffff;
}

.header-section {
  text-align: center;
  margin-bottom: 16px;

  .emblem-container {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 48px;
    height: 48px;
    background-color: #f0f9ff;
    border-radius: 50%;
    margin-bottom: 10px;

    .emblem-icon {
      font-size: 24px;
      color: #0284c7;
    }
  }

  .title {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 6px 0;
  }

  .subtitle {
    font-size: 13px;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
  }
}

.info-alert {
  margin-bottom: 18px;
  border-radius: 8px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;

  ::v-deep .el-alert__title {
    font-size: 13px;
    color: #334155;
    font-weight: 600;
  }
  ::v-deep .el-alert__description {
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
  }
}

.locked-type-banner {
  margin-bottom: 18px;
  display: flex;

  .locked-type-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    width: 100%;
    justify-content: center;

    &.household {
      background: #f0fdf4;
      color: #166534;
      border: 1px solid #bbf7d0;
    }

    &.non_household {
      background: #fffbeb;
      color: #92400e;
      border: 1px solid #fde68a;
    }

    i {
      font-size: 16px;
    }
  }
}

.auto-verify-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 20px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  margin-bottom: 20px;

  .loading-spinner-wrap {
    font-size: 32px;
    color: #0284c7;

    .auto-spin-icon {
      animation: rotating 1.5s linear infinite;
    }
  }

  .auto-verify-text {
    .auto-verify-title {
      font-size: 16px;
      font-weight: 700;
      color: #0369a1;
      margin-bottom: 4px;
    }

    .auto-verify-desc {
      font-size: 14px;
      color: #0f172a;

      .account-badge {
        color: #0284c7;
        font-family: monospace;
        font-size: 15px;
      }
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.entry-form {
  margin-top: 10px;

  .type-selector {
    display: flex;
    width: 100%;

    ::v-deep .el-radio-button {
      flex: 1;

      .el-radio-button__inner {
        width: 100%;
        padding: 12px 10px;
      }
    }
  }

  .method-selector-wrap {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: -6px;
    margin-bottom: 16px;
    padding: 8px 12px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px dashed #cbd5e1;

    .method-label {
      font-size: 13px;
      color: #475569;
      font-weight: 500;
    }

    .method-group {
      ::v-deep .el-radio-button__inner {
        padding: 6px 14px;
        font-size: 12px;
      }
    }
  }

  .field-hint {
    font-size: 12px;
    color: #6b7280;
    margin-top: 6px;
    line-height: 1.4;
  }

  .error-box {
    margin-bottom: 15px;
  }

  .action-item {
    margin-top: 25px;
    margin-bottom: 10px;

    .submit-btn {
      width: 100%;
      height: 48px;
      font-size: 16px;
      font-weight: 600;
      border-radius: 8px;
    }
  }
}

.footer-note {
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #6b7280;

  .login-link {
    color: #1890ff;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
